import { Typography, Paper, Button } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { dispatchReminderEmail } from '../../redux/slice/organization-dashboard/organization-api';
import {
  OrganizationProps,
  UserEngagementData,
} from '../../redux/slice/organization-dashboard/organization.types';
import { fetchEngagement } from '../../redux/slice/organization-dashboard/organizationthunk';
import { AppDispatch, RootState } from '../../redux/store';
import logger from '../../services/logger';
import { GenericDataTable } from '../common/GenericDataTable';

export const PlatformUsageStats: React.FC<OrganizationProps> = ({ orgnizationId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { engagement, engagementLoader } = useSelector(
    (state: RootState) => state.organizationDashboard,
  );

  const [filters, setFilters] = useState({
    name: '',
    email: '',
  });

  const data: UserEngagementData[] = engagement?.employeeData ?? [];
  const [emailLoader, setEmailLoader] = useState(false);

  const limit = 100;

  useEffect(() => {
    if (orgnizationId) {
      void dispatch(fetchEngagement({ organizationId: orgnizationId, limit: limit }));
    }
  }, [dispatch, orgnizationId, filters]);

  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const filteredData = data
    .map((user) => {
      const lastLoginDate = user.lastLoggedInDate ? new Date(user.lastLoggedInDate) : null;
      const today = new Date();
      const daysSinceLastLogin = lastLoginDate
        ? Math.floor((today.getTime() - lastLoginDate.getTime()) / (1000 * 60 * 60 * 24))
        : 'N/A';

      return { ...user, daysSinceLastLogin };
    })
    .filter((user) => {
      return (
        (!filters.name || user?.name?.toLowerCase().includes(filters.name)) &&
        (!filters.email || user?.email?.toLowerCase().includes(filters.email))
      );
    });

  const handleButtonClick = async (user: UserEngagementData) => {
    try {
      setEmailLoader(true);
      if (user) {
        const sendEmail = await dispatchReminderEmail(user);
        if (sendEmail?.status == 200) {
          toast.success(`Email sent to ${user.email}`);
        }
      }
    } catch (error) {
      toast.error('Something went wrong...');
      logger.error('error:', error);
    } finally {
      setEmailLoader(false);
    }
  };

  const columns = [
    { label: 'Name', accessor: 'name' },
    { label: 'Email', accessor: 'email' },
    { label: 'Time Spent', accessor: 'timeSpentInSeconds' },
    { label: 'Last Logged-In (In days)', accessor: 'daysSinceLastLogin' },
    { label: 'Last Logged In', accessor: 'lastLoggedInDate' },
    { label: 'Registration Date', accessor: 'createdAt' },
    {
      label: 'Send Email',
      accessor: 'action',
      Cell: (row: UserEngagementData) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={() => handleButtonClick(row)}
        >
          Reminder
        </Button>
      ),
    },
  ];

  return (
    <Paper className="p-6">
      <Typography
        variant="h6"
        fontWeight="bold"
        className="border-purple-900 border-b-2 rounded-lg  mb-4 mt-4 text-purple-700 "
        gutterBottom
      >
        User Engagement Data
      </Typography>

      <GenericDataTable
        isLoading={engagementLoader}
        columns={columns}
        data={filteredData}
        filterFields={[
          { name: 'name', label: 'Filter by Name' },
          { name: 'email', label: 'Filter by Email' },
        ]}
        onFilterChange={handleFilterChange}
        ctaLoader={emailLoader}
      />
    </Paper>
  );
};
